import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
} from "@chakra-ui/react";
import { Calendar, Hash, Search, User, Venus } from "lucide-react";
import Select from "react-select";

// ─── Shared react-select styles ───────────────────────────────────────────────
const selectStyles = {
  container: (base) => ({ ...base, width: "100%" }),
  control: (base, state) => ({
    ...base,
    minHeight: "40px",
    borderRadius: "8px",
    borderColor: state.isFocused ? "#3182ce" : "#E2E8F0",
    boxShadow: state.isFocused ? "0 0 0 1px #3182ce" : "none",
    fontSize: "14px",
    "&:hover": { borderColor: "#CBD5E0" },
    backgroundColor: "#FAFAFA",
    transition: "all 0.15s",
  }),
  placeholder: (base) => ({ ...base, color: "#A0AEC0", fontSize: "14px" }),
  option: (base, state) => ({
    ...base,
    fontSize: "14px",
    backgroundColor: state.isSelected
      ? "#EBF8FF"
      : state.isFocused
        ? "#F7FAFC"
        : "white",
    color: state.isSelected ? "#2B6CB0" : "#2D3748",
    fontWeight: state.isSelected ? "500" : "normal",
  }),
  singleValue: (base) => ({ ...base, color: "#2D3748", fontSize: "14px" }),
  menu: (base) => ({
    ...base,
    borderRadius: "8px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
    border: "1px solid #E2E8F0",
    overflow: "hidden",
  }),
  clearIndicator: (base) => ({
    ...base,
    color: "#A0AEC0",
    "&:hover": { color: "#718096" },
  }),
  dropdownIndicator: (base) => ({ ...base, color: "#A0AEC0" }),
};

// ─── Shared input styles ──────────────────────────────────────────────────────
const inputStyles = {
  size: "md",
  borderRadius: "lg",
  bg: "gray.50",
  border: "1px solid",
  borderColor: "gray.200",
  fontSize: "sm",
  color: "gray.700",
  _placeholder: { color: "gray.400" },
  focusBorderColor: "blue.400",
  _hover: { borderColor: "gray.300" },
  _focus: { bg: "white" },
  transition: "all 0.15s",
};

// ─── Field wrapper ────────────────────────────────────────────────────────────
const Field = ({ label, icon, children, mb = 4 }) => (
  <FormControl mb={mb}>
    <FormLabel
      mb={1.5}
      fontSize="xs"
      fontWeight="600"
      textTransform="uppercase"
      letterSpacing="0.06em"
      color="gray.500"
    >
      <HStack spacing={1.5}>
        {icon && <Icon as={icon} boxSize="12px" />}
        <Text as="span">{label}</Text>
      </HStack>
    </FormLabel>
    {children}
  </FormControl>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const SearchPersonForm = ({
  handleSubmit,
  onSubmit,
  register,
  setValue,
  isLoading,
}) => {
  return (
    <Box
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      bg="white"
      borderRadius="xl"
      border="1px solid"
      borderColor="gray.200"
      boxShadow="sm"
      overflow="hidden"
    >
      {/* Header */}
      <Box
        px={5}
        py={4}
        borderBottom="1px solid"
        borderColor="gray.100"
        bg="gray.50"
      >
        <HStack spacing={2}>
          <Icon as={Search} color="blue.500" boxSize="16px" />
          <Text
            fontSize="sm"
            fontWeight="700"
            color="gray.700"
            letterSpacing="-0.01em"
          >
            Search Persons
          </Text>
        </HStack>
        <Text fontSize="xs" color="gray.400" mt={0.5}>
          Enter one or more fields to filter results
        </Text>
      </Box>

      {/* Form Body */}
      <Box px={5} py={5}>
        <Field label="First Name" icon={User}>
          <InputGroup>
            <InputLeftElement pointerEvents="none" h="40px">
              <Icon as={User} color="gray.300" boxSize="14px" />
            </InputLeftElement>
            <Input
              pl={9}
              placeholder="e.g. John"
              {...inputStyles}
              {...register("FIRST_NAME")}
            />
          </InputGroup>
        </Field>

        <Field label="Last Name" icon={User}>
          <InputGroup>
            <InputLeftElement pointerEvents="none" h="40px">
              <Icon as={User} color="gray.300" boxSize="14px" />
            </InputLeftElement>
            <Input
              pl={9}
              placeholder="e.g. Doe"
              {...inputStyles}
              {...register("LAST_NAME")}
            />
          </InputGroup>
        </Field>

        <Field label="Person ID" icon={Hash}>
          <InputGroup>
            <InputLeftElement pointerEvents="none" h="40px">
              <Icon as={Hash} color="gray.300" boxSize="14px" />
            </InputLeftElement>
            <Input
              pl={9}
              placeholder="Enter person ID"
              {...inputStyles}
              {...register("id")}
            />
          </InputGroup>
        </Field>

        <Divider borderColor="gray.100" mb={2} />

        <Field label="Gender" icon={Venus}>
          <Select
            styles={selectStyles}
            options={[
              { value: "M", label: "Male" },
              { value: "F", label: "Female" },
            ]}
            placeholder="Select..."
            isClearable
            onChange={(opt) => setValue("GENDER", opt?.value || "")}
          />
        </Field>

        <Field label="Date of Birth" icon={Calendar} mb={0}>
          <InputGroup>
            <InputLeftElement pointerEvents="none" h="40px">
              <Icon as={Calendar} color="gray.300" boxSize="14px" />
            </InputLeftElement>
            <Input
              type="date"
              pl={9}
              {...inputStyles}
              {...register("BIRTH_DATE")}
            />
          </InputGroup>
        </Field>
      </Box>

      {/* Footer / Submit */}
      <Box px={5} pb={5}>
        <Button
          type="submit"
          isLoading={isLoading}
          loadingText="Searching..."
          w="full"
          size="md"
          borderRadius="lg"
          colorScheme="blue"
          leftIcon={<Icon as={Search} boxSize="14px" />}
          fontWeight="600"
          fontSize="sm"
          letterSpacing="0.01em"
          boxShadow="sm"
          _hover={{ transform: "translateY(-1px)", boxShadow: "md" }}
          _active={{ transform: "translateY(0)" }}
          transition="all 0.15s"
        >
          Search
        </Button>
      </Box>
    </Box>
  );
};

export default SearchPersonForm;
